/**
 * TextResultModal Component
 * Displays QA/text model results with answer, citations, and metadata.
 * Supports both OpenScholar (citations, papers) and MedFound (medical LLM) output formats.
 */
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './TextResultModal.css';

const TextResultModal = ({ execution, onClose }) => {
  const [copied, setCopied] = useState(false);

  if (!execution || !execution.text_output) return null;

  const result = execution.text_output;
  const { answer, query, patient_context, citations, keywords_extracted, model_used, papers_retrieved } = result;

  // Generic model info: works for both OpenScholar (model_used) and MedFound (model)
  const modelDisplay = model_used || result.model || null;

  const handleCopyAnswer = () => {
    navigator.clipboard.writeText(answer || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Format the date
  const dateStr = execution.completed_at
    ? new Date(execution.completed_at).toLocaleString()
    : execution.created_at
      ? new Date(execution.created_at).toLocaleString()
      : null;

  return (
    <div className="text-result-overlay" onClick={onClose}>
      <div className="text-result-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="text-result-header">
          <div className="text-result-title">
            <i
              className={`fa ${execution.model_icon || 'fa-graduation-cap'}`}
              style={{ color: execution.model_color || '#17a2b8' }}
            ></i>
            <span>{execution.model_name || 'QA Result'}</span>
            {dateStr && (
              <span className="text-result-date">{dateStr}</span>
            )}
          </div>
          <button className="text-result-close" onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        {/* Query */}
        {query && (
          <div className="text-result-query">
            <div className="text-result-query-label">
              <i className="fa fa-question-circle"></i>
              Query
            </div>
            <div className="text-result-query-text">{query}</div>
          </div>
        )}

        {/* Patient Context */}
        {patient_context && (
          <div className="text-result-context">
            <div className="text-result-context-label">
              <i className="fa fa-user-md"></i>
              Patient Context
            </div>
            <div className="text-result-context-text">
              {typeof patient_context === 'string'
                ? patient_context
                : Object.entries(patient_context).map(([key, value]) =>
                    value ? (
                      <div key={key}>
                        <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : value}
                      </div>
                    ) : null
                  )
              }
            </div>
          </div>
        )}

        {/* Error */}
        {result.status === 'error' && (
          <div className="text-result-error">
            <i className="fa fa-exclamation-triangle"></i>
            <span>{result.error || 'An unknown error occurred'}</span>
          </div>
        )}

        {/* Answer */}
        {result.status !== 'error' && (
          <div className="text-result-answer">
            <div className="text-result-answer-header">
              <span>Answer</span>
              <button
                className="text-result-copy-btn"
                onClick={handleCopyAnswer}
                title="Copy answer"
              >
                <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="text-result-answer-body">
              <ReactMarkdown>{answer || 'No answer generated.'}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Citations */}
        {citations && citations.length > 0 && (
          <div className="text-result-citations">
            <div className="text-result-citations-header">
              <i className="fa fa-book"></i>
              <span>References ({citations.length})</span>
            </div>
            <div className="text-result-citations-list">
              {citations.map((cite, idx) => (
                <div key={idx} className="text-result-citation">
                  <span className="citation-index">[{cite.index ?? idx + 1}]</span>
                  <div className="citation-content">
                    <span className="citation-title">{cite.title}</span>
                    {cite.url && (
                      <a
                        href={cite.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="citation-link"
                      >
                        <i className="fa fa-external-link-alt"></i>
                        View Paper
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata footer */}
        <div className="text-result-meta">
          {modelDisplay && (
            <span className="text-result-meta-item">
              <i className="fa fa-robot"></i> {modelDisplay}
            </span>
          )}
          {result.elapsed_seconds != null && (
            <span className="text-result-meta-item">
              <i className="fa fa-clock"></i> {result.elapsed_seconds}s inference
            </span>
          )}
          {result.temperature != null && (
            <span className="text-result-meta-item">
              <i className="fa fa-thermometer-half"></i> temp: {result.temperature}
            </span>
          )}
          {papers_retrieved != null && (
            <span className="text-result-meta-item">
              <i className="fa fa-file-alt"></i> {papers_retrieved} papers retrieved
            </span>
          )}
          {keywords_extracted && keywords_extracted.length > 0 && (
            <span className="text-result-meta-item">
              <i className="fa fa-search"></i> Keywords: {keywords_extracted.join(', ')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextResultModal;
